import { useApolloClient } from '@apollo/client'
import { bookQuery } from 'api/queries'
import type { CoverBookShelfProps } from 'components/modular/shelves/book-shelf/BookShelf.types'
import { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { INTERMEDIATE_DETAILS_BOOK_PATH } from 'routes/paths'
import { post_event } from 'utils/analytics'
import { getBooksBadges } from 'utils/badges'
import { useFeatureToggles } from 'utils/hooks/use-feature-toggles/useFeatureToggles'
import { useUserInfo } from 'utils/hooks/use-userInfo'
import { Degree } from 'utils/user'
import {
  readerOlympicsShelvesControllerList,
  type ReaderOlympicsShelvesControllerListParams
} from 'api.gen'
import { OLYMPICS_SHELF_TITLE } from './OlympicsShelf.constants'
import { shouldUseNextjsBookIntermediate } from 'utils/identify-modules/IdentifyModules'

type ReaderShelfBook = {
  id: number
  name: string | null
  slug: string | null
  image: string | null
  author: string | null
  order: number | null
}

type ReaderShelf = {
  id: number
  name: string
  subtitle: string | null
  books: ReaderShelfBook[]
}

type ShelfWithBooks = {
  shelf: ReaderShelf
  books: CoverBookShelfProps[]
}

const resolveUserParams = (
  user: ReturnType<typeof useUserInfo>['user'],
  isStudentView: boolean,
  studentDegree?: string
): ReaderOlympicsShelvesControllerListParams => {
  const params: ReaderOlympicsShelvesControllerListParams = {}

  const readerRootId = user?.reader?.root?.id
  const teacherRootId = user?.teacher?.root?.id
  const rootId = readerRootId ?? teacherRootId
  if (typeof rootId === 'number') {
    params.rootId = rootId
  }

  if (isStudentView && studentDegree) {
    params.degree = studentDegree
    return params
  }

  const isTeacher = Boolean(user?.teacher?.id)
  if (isTeacher) {
    params.degree = 'PROFESSORES'
    return params
  }

  const userDegree = Degree.getDegree(user)
  if (userDegree) {
    params.degree = userDegree
  }

  return params
}

const useOlympicsShelf = (isStudentView: boolean, degree?: string) => {
  const client = useApolloClient()
  const history = useHistory()
  const { user } = useUserInfo()
  const { featureToggles } = useFeatureToggles()
  const [shelves, setShelves] = useState<ShelfWithBooks[]>([])
  const [loading, setLoading] = useState(true)

  const isEnabled = featureToggles.olympicsShelves

  useEffect(() => {
    if (!isEnabled) {
      setLoading(false)
      return
    }

    fetchShelves()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnabled, degree, user?.reader?.root?.id, user?.teacher?.root?.id])

  const fetchShelfBooks = async (
    shelf: ReaderShelf
  ): Promise<CoverBookShelfProps[]> => {
    const results = await Promise.all(
      shelf.books.map(book => {
        if (!book.slug) return null
        return client
          .query({ query: bookQuery, variables: { slug: book.slug } })
          .then(res => res.data?.bookDetails)
          .catch(() => null)
      })
    )

    return results.filter(Boolean).map(book => {
      const tag = getBooksBadges({ book, user })
      return {
        title: book.name,
        slug: book.slug,
        author: book.author,
        imageUrlThumb: book.imageUrlThumb,
        hasAudioBook: book.hasAudioBook,
        language: book.language,
        premiumLock: book.premiumLock,
        degreesRange: tag && [{ tagName: tag?.name, fullName: tag?.fullName }]
      }
    })
  }

  const fetchShelves = async () => {
    setLoading(true)

    try {
      const params = resolveUserParams(user, isStudentView, degree)
      const response = (await readerOlympicsShelvesControllerList(
        params
      )) as unknown as { data: ReaderShelf[] | null | undefined }
      const readerShelves = response?.data ?? []

      const shelvesWithBooks = await Promise.all(
        readerShelves.map(async shelf => ({
          shelf,
          books: await fetchShelfBooks(shelf)
        }))
      )

      setShelves(shelvesWithBooks.filter(item => item.books.length > 0))
    } catch {
      setShelves([])
    } finally {
      setLoading(false)
    }
  }

  const onClickBook = (book: CoverBookShelfProps) => {
    post_event(`${OLYMPICS_SHELF_TITLE}/ClickCapaLivro/LIVROS`, book.slug)
    if (shouldUseNextjsBookIntermediate()) {
      window.location.href = `/app-v2/book/intermediate/${book.slug}`
      return
    }
    history.push(INTERMEDIATE_DETAILS_BOOK_PATH(book.slug))
  }

  return {
    shelves,
    loading,
    isEnabled,
    onClickBook
  }
}

export { useOlympicsShelf }
export type { ReaderShelf, ReaderShelfBook, ShelfWithBooks }
